import { Link } from 'react-router-dom'
import '../WeldingToolboxLegal/index.scss'

const CONTACT_EMAIL = 'grxinterstellar@gmail.com'
const LAST_UPDATED = '26 July 2026'
const SUPPORT_CONTACT_URL =
  '/contact?subject=Smart%20TV%20Remote%20support%20request'

const supportTopics = [
  {
    frTitle: 'Detection et connexion',
    frBody:
      "Verifiez que le telephone et le televiseur utilisent le meme reseau Wi-Fi. Relancez la recherche, puis acceptez la demande d'autorisation affichee sur le televiseur.",
    enTitle: 'Discovery and connection',
    enBody:
      'Make sure the phone and TV are connected to the same Wi-Fi network. Run discovery again, then accept the authorization request displayed on the TV.',
  },
  {
    frTitle: 'Commandes de la telecommande',
    frBody:
      "Si une commande ne repond pas, indiquez la marque, le modele et l'annee approximative du televiseur, ainsi que la commande concernee.",
    enTitle: 'Remote commands',
    enBody:
      'If a command does not respond, include the TV brand, model, approximate year, and the affected command in your message.',
  },
  {
    frTitle: 'Diffusion de medias',
    frBody:
      "L'acces aux photos ou videos est demande uniquement lorsque vous choisissez un media a diffuser. Certains formats ou modeles de televiseurs peuvent ne pas etre compatibles.",
    enTitle: 'Media casting',
    enBody:
      'Photo or video access is requested only when you choose media to cast. Some formats or TV models may not be compatible.',
  },
  {
    frTitle: 'Premium et publicites',
    frBody:
      "Pour une question concernant un achat, joignez la plateforme utilisee et le message d'erreur, sans envoyer de coordonnees bancaires.",
    enTitle: 'Premium and advertising',
    enBody:
      'For purchase questions, include the platform and error message, but never send payment card details.',
  },
]

const privacySections = [
  {
    frTitle: '1. Donnees traitees',
    frBody:
      "Smart TV Remote ne demande pas de compte utilisateur. Pour detecter et controler un televiseur, l'application peut traiter sur votre appareil des informations techniques du reseau local, notamment le nom, la marque, le modele, l'adresse IP, l'adresse MAC, le protocole et les jetons d'autorisation du televiseur.",
    enTitle: '1. Data processed',
    enBody:
      'Smart TV Remote does not require a user account. To discover and control a TV, the app may process local-network technical information on your device, including the TV name, brand, model, IP address, MAC address, protocol, and authorization tokens.',
  },
  {
    frTitle: '2. Donnees stockees localement',
    frBody:
      "Les televiseurs associes, preferences, favoris, historiques de commandes et macros sont enregistres localement sur votre appareil. Ces donnees peuvent etre supprimees en effacant les donnees de l'application ou en la desinstallant.",
    enTitle: '2. Data stored locally',
    enBody:
      'Paired TVs, preferences, favorites, command history, and macros are stored locally on your device. You can remove this data by clearing the app data or uninstalling the app.',
  },
  {
    frTitle: '3. Photos et videos',
    frBody:
      "Lorsque vous choisissez de diffuser un media, l'application accede uniquement aux photos ou videos que vous selectionnez. Le media peut etre prepare temporairement puis transmis au televiseur sur votre reseau local. Il n'est pas televerse vers un serveur exploite par le developpeur.",
    enTitle: '3. Photos and videos',
    enBody:
      'When you choose to cast media, the app accesses only the photos or videos you select. Media may be prepared temporarily and sent to the TV over your local network. It is not uploaded to a server operated by the developer.',
  },
  {
    frTitle: '4. Publicites et consentement',
    frBody:
      "La version gratuite peut afficher des publicites avec Google Mobile Ads. Selon votre region et vos choix de consentement, Google peut traiter des identifiants d'appareil, des donnees publicitaires, des interactions et des informations techniques conformement a sa propre politique de confidentialite.",
    enTitle: '4. Advertising and consent',
    enBody:
      'The free version may display ads through Google Mobile Ads. Depending on your region and consent choices, Google may process device identifiers, advertising data, interactions, and technical information under its own privacy policy.',
  },
  {
    frTitle: '5. Achats Premium',
    frBody:
      "Les achats et restaurations sont geres par Apple App Store ou Google Play et peuvent utiliser RevenueCat pour verifier le statut Premium. Le developpeur ne recoit pas vos coordonnees bancaires.",
    enTitle: '5. Premium purchases',
    enBody:
      'Purchases and restorations are handled by the Apple App Store or Google Play and may use RevenueCat to verify Premium status. The developer does not receive your payment card details.',
  },
  {
    frTitle: '6. Services tiers',
    frBody:
      "L'application peut utiliser les services techniques de Google, Apple, RevenueCat et Expo pour les publicites, achats, mises a jour, distribution et fonctionnement. Ces fournisseurs traitent certaines donnees selon leurs propres politiques.",
    enTitle: '6. Third-party services',
    enBody:
      'The app may use technical services from Google, Apple, RevenueCat, and Expo for advertising, purchases, updates, distribution, and operation. These providers process certain data under their own policies.',
  },
  {
    frTitle: '7. Contact',
    frBody:
      'Pour toute question relative a la confidentialite, a vos donnees ou a leur suppression, contactez-nous par email.',
    enTitle: '7. Contact',
    enBody:
      'For questions about privacy, your data, or data deletion, please contact us by email.',
  },
]

const LanguageBlock = ({ lang, title, body }) => (
  <div className="legal-language-block" lang={lang}>
    <span className="legal-lang-label">{lang === 'fr' ? 'FR' : 'EN'}</span>
    <h2>{title}</h2>
    <p>{body}</p>
  </div>
)

const SupportPage = () => (
  <>
    <p className="legal-eyebrow">Smart TV Remote</p>
    <h1>Support / Assistance</h1>
    <div className="legal-lead-group">
      <p className="legal-lead" lang="fr">
        Besoin d'aide pour detecter, connecter ou controler votre televiseur ?
        Utilisez le formulaire de contact et precisez la marque et le modele de
        votre TV.
      </p>
      <p className="legal-lead" lang="en">
        Need help discovering, connecting, or controlling your TV? Use the
        contact form and include your TV brand and model.
      </p>
    </div>

    <div className="legal-actions">
      <Link className="legal-primary-link" to={SUPPORT_CONTACT_URL}>
        Contact form / Formulaire de contact
      </Link>
      <a className="legal-secondary-link" href={`mailto:${CONTACT_EMAIL}`}>
        {CONTACT_EMAIL}
      </a>
    </div>

    <div className="legal-grid">
      {supportTopics.map((topic) => (
        <section className="legal-card" key={topic.enTitle}>
          <LanguageBlock
            lang="fr"
            title={topic.frTitle}
            body={topic.frBody}
          />
          <LanguageBlock
            lang="en"
            title={topic.enTitle}
            body={topic.enBody}
          />
        </section>
      ))}
    </div>

    <div className="legal-footer-box">
      <p>
        Privacy Policy / Politique de confidentialite:{' '}
        <Link to="/smart-tv-remote-privacy">smart-tv-remote-privacy</Link>
      </p>
      <p>Last updated / Derniere mise a jour : {LAST_UPDATED}</p>
    </div>
  </>
)

const PrivacyPage = () => (
  <>
    <p className="legal-eyebrow">Smart TV Remote</p>
    <h1>Privacy Policy / Politique de confidentialite</h1>
    <div className="legal-lead-group">
      <p className="legal-lead" lang="fr">
        Cette politique explique quelles donnees sont traitees par Smart TV
        Remote et comment elles sont utilisees.
      </p>
      <p className="legal-lead" lang="en">
        This policy explains what data Smart TV Remote processes and how it is
        used.
      </p>
    </div>
    <p className="legal-date">
      Last updated / Derniere mise a jour : {LAST_UPDATED}
    </p>

    <div className="legal-section-list">
      {privacySections.map((section) => (
        <section key={section.enTitle}>
          <LanguageBlock
            lang="fr"
            title={section.frTitle}
            body={section.frBody}
          />
          <LanguageBlock
            lang="en"
            title={section.enTitle}
            body={section.enBody}
          />
        </section>
      ))}
    </div>

    <div className="legal-footer-box">
      <p>
        Contact: <Link to={SUPPORT_CONTACT_URL}>contact form</Link>
        {' / '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
      </p>
      <p>
        Support / Assistance:{' '}
        <Link to="/smart-tv-remote-support">smart-tv-remote-support</Link>
      </p>
    </div>
  </>
)

const SmartTvRemoteLegal = ({ page }) => (
  <main className="welding-legal-page">
    <div className="legal-shell">
      <Link className="legal-home-link" to="/">
        Lamine Messaci
      </Link>
      {page === 'support' ? <SupportPage /> : <PrivacyPage />}
    </div>
  </main>
)

export default SmartTvRemoteLegal
